// slices/partySlice.ts
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { HttpStatusCode } from 'constantsValues';
import { Party } from 'generated-api/bff/api'; // Import Party type from the Party API
import { getApiInstance } from 'services/Api';

// Async thunk for fetching party data
export const fetchParty = createAsyncThunk<Party>('party/fetchParty', async () => {
	const apiInstance = await getApiInstance('/commercial-selfservice/');
	const response = await apiInstance.bffApiServiceLayer.getParty();

	if (response.status === HttpStatusCode.NoContent) {
		return;
	}

	if (!response.data) {
		throw new Error('No party data found');
	}
	return response.data;
});

interface PartyState {
	data: Party | null;
	status: HttpStatusCode | null;
	error: string | null;
}

const initialState: PartyState = {
	data: null,
	status: HttpStatusCode.Idle,
	error: null,
};

// Slice for party
const partySlice = createSlice({
	name: 'party',
	initialState,
	reducers: {
		clearPartyCache(state) {
			state.data = null;
			state.status = HttpStatusCode.Idle;
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchParty.pending, (state) => {
				state.status = HttpStatusCode.Loading;
				state.error = null;
			})
			.addCase(fetchParty.fulfilled, (state, action) => {
				if (!action.payload) {
					state.status = HttpStatusCode.NoContent;
					return;
				}

				state.status = HttpStatusCode.OK;
				state.data = action.payload;
			})
			.addCase(fetchParty.rejected, (state, action) => {
				state.status = HttpStatusCode.InternalServerError;
				state.error = action.error.message || 'Failed to fetch party data';
			});
	},
});

export const { clearPartyCache } = partySlice.actions;
export default partySlice.reducer;
